import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NO_COLOR,
  pickImageFile,
  toastManager,
  VisualPicker,
  type VisualPickerAction,
} from "@salestrak/ui";
import { AvatarEditTrigger } from "#app/components/settings/avatar-edit-trigger";
// Imported off the module, not the entity barrel: the barrel re-exports the
// profile island, which renders this editor — going through it would cycle.
import { EntityVisual, type EntityVisualSize } from "#app/components/entity/entity-visual";
import { UpdateProfilePhotoDialog } from "#app/components/settings/update-profile-photo-dialog";
import { useAvatarEditor } from "#app/lib/hooks/use-avatar-editor";
import { useAvatarUpload } from "#app/lib/hooks/use-avatar-upload";
import { resolveAvatarColor } from "#app/lib/entity-visuals";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import { decodeImageOrThrow } from "#app/lib/media-process";
import type { ProfileFormUser } from "#app/lib/data";

// The fields the editor reads: identity for the avatar itself, the id for
// the mutations. Every User fragment that carries an avatar satisfies it —
// the settings form's row and the profile island's roster-backed header.
export type ProfileAvatarUser = Pick<
  ProfileFormUser,
  "userId" | "name" | "username" | "email" | "profileImage" | "avatarColor"
>;

interface ProfileAvatarProps {
  user: ProfileAvatarUser;
  /** Avatar circle size. Settings rows sit at "md"; the profile island's
   *  identity header is "xl". */
  size?: Extract<EntityVisualSize, "md" | "xl">;
  /** Live presence dot — the profile island's header carries it, the
   *  settings row does not. */
  presence?: boolean;
}

/**
 * The editable avatar on the profile surfaces (the settings row and the
 * profile island header): pressing it opens the VisualPicker — the curated
 * Color palette plus the explicit "No color" choice, with photo upload (the
 * crop-and-zoom pipeline) and photo removal as its action rows. The trigger
 * reveals an edit scrim on hover/focus.
 *
 * The photo rows ride the PHOTOS-flagged media store, so they are offered
 * only where it is on; a colour is not a media write and is always offered.
 */
export function ProfileAvatar({ user, size = "md", presence = false }: ProfileAvatarProps) {
  // `hasFlag` answers false until the flags resolve — and again while a
  // workspace switch re-resolves them — so the rows stay while it is in
  // flight rather than blinking out. The crop dialog below is outside this
  // gate for the same reason: dropping it mid-switch would discard an
  // in-progress crop and revoke the picked file's object URL.
  const { ready, hasFlag } = useFeatureFlags();
  const photosEnabled = !ready || hasFlag(FeatureFlagKey.PHOTOS);
  const { uploadAvatar } = useAvatarUpload();
  const { setColor, removePhoto } = useAvatarEditor(user.userId);
  const [imageUri, setImageUri] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Object URLs hold the picked file's bytes until explicitly revoked.
  useEffect(() => {
    return () => {
      if (imageUri) URL.revokeObjectURL(imageUri);
    };
  }, [imageUri]);

  const handlePick = useCallback(async () => {
    const file = await pickImageFile();
    if (!file) return;
    // Gate the format BEFORE opening the cropper. The cropper decodes via an
    // HTMLImageElement, which fails silently on HEIC (the iPhone default) —
    // leaving a blank, dead-end dialog. Reuse the upload pipeline's decode gate
    // so an unsupported file gets the same actionable message here instead.
    try {
      (await decodeImageOrThrow(file)).close();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : "That image isn't supported",
        type: "error",
      });
      return;
    }
    setImageUri(URL.createObjectURL(file));
    setDialogOpen(true);
  }, []);

  const handleSave = useCallback(
    (image: Blob) => uploadAvatar(user.userId, image),
    [uploadAvatar, user.userId],
  );

  // Ring the EFFECTIVE color: chosen, or the hash default when never picked;
  // the slash swatch rings when "none" (themed) was explicitly chosen.
  const effectiveColor =
    user.avatarColor === NO_COLOR
      ? NO_COLOR
      : (resolveAvatarColor(user.userId, user.avatarColor) ?? NO_COLOR);

  const actions = useMemo<VisualPickerAction[]>(() => {
    if (!photosEnabled) return [];
    const rows: VisualPickerAction[] = [
      { icon: "upload", label: "Upload avatar", onClick: () => void handlePick() },
    ];
    if (user.profileImage) {
      rows.push({
        icon: "x-circle",
        label: "Remove avatar",
        onClick: () => void removePhoto(),
      });
    }
    return rows;
  }, [handlePick, photosEnabled, removePhoto, user.profileImage]);

  return (
    <>
      <VisualPicker
        noColor
        color={effectiveColor}
        onColorChange={(color) => void setColor(color)}
        actions={actions}
        tooltip="Set avatar"
        trigger={
          <AvatarEditTrigger label="Set avatar">
            <EntityVisual
              entity={{ kind: "user", id: user.userId, user }}
              presence={presence}
              size={size}
              // Profile surface — show the TRUE color (viewer-themed rule off)
              // so the picker's choice is visible where it's made.
              trueAvatarColor
            />
          </AvatarEditTrigger>
        }
      />
      {imageUri ? (
        <UpdateProfilePhotoDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          imageUri={imageUri}
          onSave={handleSave}
        />
      ) : null}
    </>
  );
}
